import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  UserPlus,
  Mail,
  Phone,
  Globe,
  Shield,
  Briefcase,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Copy,
  Eye,
  EyeOff,
  Key,
  Users,
  Plus,
  Trash2,
  User,
  Calendar,
  CreditCard,
  Crown
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  "México", "Colombia", "Argentina", "Perú", "Chile", "Ecuador", "Venezuela", 
  "Guatemala", "Honduras", "El Salvador", "Nicaragua", "Costa Rica", "Panamá", 
  "República Dominicana", "Brasil", "España", "Otro"
];

const RELATIONSHIPS = [
  { id: "titular", name: "Titular (Jefe/a de Familia)" },
  { id: "spouse", name: "Cónyuge" },
  { id: "child", name: "Hijo/a" },
  { id: "parent", name: "Padre/Madre" },
  { id: "sibling", name: "Hermano/a" },
  { id: "other", name: "Otro Familiar" }
];

const AdminCreateClient = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [vendedores, setVendedores] = useState([]);
  const [oficiales, setOficiales] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [createdClient, setCreatedClient] = useState(null);
  const [step, setStep] = useState(1); // 1: info básica, 2: familia, 3: asignación

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    visa_type: "B1/B2",
    country: "México",
    initial_risk_level: "medium",
    vendedor_id: "",
    oficial_id: "",
    plan_type: "estandar",
    authorized_persons: 1,
    is_vip_partner: false  // NEW: Create client as VIP Partner from the start
  });

  const [familyMembers, setFamilyMembers] = useState([
    { name: "", relationship: "titular", birth_date: "", passport_number: "", nationality: "México" }
  ]);

  useEffect(() => {
    fetchStaff();
  }, []);

  // Sync titular name with form name
  useEffect(() => {
    if (familyMembers[0] && familyMembers[0].relationship === "titular") {
      const updated = [...familyMembers];
      updated[0].name = formData.name;
      updated[0].nationality = formData.country;
      setFamilyMembers(updated);
    }
  }, [formData.name, formData.country]);

  const fetchStaff = async () => {
    try {
      const [vendRes, ofcRes] = await Promise.all([
        axios.get(`${API}/admin/staff/vendedores`),
        axios.get(`${API}/admin/staff/oficiales`)
      ]);
      setVendedores(vendRes.data);
      setOficiales(ofcRes.data);
    } catch (error) {
      toast.error("Error al cargar staff");
    } finally {
      setLoadingStaff(false);
    }
  };

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let pwd = "";
    for (let i = 0; i < 10; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password: pwd });
    setShowPassword(true);
  };

  const addFamilyMember = () => {
    setFamilyMembers([
      ...familyMembers,
      { name: "", relationship: "spouse", birth_date: "", passport_number: "", nationality: formData.country }
    ]);
  };

  const removeFamilyMember = (index) => {
    if (index === 0) {
      toast.error("No puedes eliminar al titular");
      return;
    }
    const updated = familyMembers.filter((_, i) => i !== index);
    setFamilyMembers(updated);
  };

  const updateFamilyMember = (index, field, value) => {
    const updated = [...familyMembers];
    updated[index][field] = value;
    setFamilyMembers(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error("Nombre y email son requeridos");
      return;
    }
    
    // Vendedor and Oficial are now optional (vendor system eliminated - admin handles directly)

    // Validate family members
    for (let i = 0; i < familyMembers.length; i++) {
      if (!familyMembers[i].name) {
        toast.error(`El nombre del miembro ${i + 1} es requerido`);
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        family_members: familyMembers,
        authorized_persons: formData.authorized_persons
      };
      const response = await axios.post(`${API}/admin/clients`, payload);
      setCreatedClient(response.data);
      toast.success("Cliente creado exitosamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear cliente");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  // Check if staff is complete - No longer required since vendor system was removed
  const staffComplete = true;  // Always true - staff assignment is now optional

  if (createdClient) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl border border-navy-100 p-8 text-center"
          >
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-navy-900 mb-2">¡Cliente Creado!</h2>
            <p className="text-navy-500 mb-6">
              El cliente ha sido registrado exitosamente con {familyMembers.length} persona(s) en el caso.
            </p>

            <div className="bg-navy-50 rounded-xl p-6 text-left mb-6">
              <h3 className="font-semibold text-navy-900 mb-4">Credenciales de Acceso</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-navy-600">Email:</span>
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-3 py-1 rounded text-navy-900">
                      {createdClient.user?.email}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(createdClient.user?.email)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-navy-600">Contraseña:</span>
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-3 py-1 rounded text-navy-900">
                      {createdClient.temp_password}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(createdClient.temp_password)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Family Summary */}
            <div className="bg-blue-50 rounded-xl p-4 text-left mb-6">
              <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Miembros del Caso ({familyMembers.length})
              </h4>
              <div className="space-y-1">
                {familyMembers.map((member, idx) => (
                  <div key={idx} className="text-sm text-blue-700">
                    {idx + 1}. {member.name} - {RELATIONSHIPS.find(r => r.id === member.relationship)?.name}
                  </div>
                ))}
              </div>
            </div>

            {/* VIP Partner Info - Show prominently if VIP Partner */}
            {(createdClient.user?.is_vip_partner || createdClient.user?.referral_code) && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 text-left mb-6 border-2 border-amber-300">
                <h4 className="font-bold text-amber-900 mb-4 flex items-center gap-2 text-lg">
                  <Crown className="w-6 h-6 text-amber-600" />
                  🎉 VIP Partner Activado
                </h4>
                
                <div className="bg-white rounded-lg p-4 border border-amber-200">
                  <p className="text-sm text-amber-700 mb-2">Código de Referido:</p>
                  <div className="flex items-center justify-between">
                    <code className="bg-amber-100 px-6 py-3 rounded-lg text-amber-900 font-bold text-2xl tracking-widest border-2 border-amber-300">
                      {createdClient.user?.referral_code || "Generando..."}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      onClick={() => copyToClipboard(createdClient.user?.referral_code)}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copiar
                    </Button>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-white rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800 mb-2"><strong>Link para compartir:</strong></p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-amber-50 px-3 py-2 rounded text-amber-900 text-sm break-all">
                      https://dividelo.com/r/{createdClient.user?.referral_code}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(`https://dividelo.com/r/${createdClient.user?.referral_code}`)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <p className="text-xs text-amber-600 mt-4">
                  ✨ El cliente puede compartir este código para ganar comisiones por cada referido.
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => {
                setCreatedClient(null);
                setFormData({
                  name: "", email: "", phone: "", password: "",
                  visa_type: "B1/B2", country: "México",
                  initial_risk_level: "medium", vendedor_id: "", oficial_id: "", plan_type: "estandar",
                  authorized_persons: 1, is_vip_partner: false
                });
                setFamilyMembers([{ name: "", relationship: "titular", birth_date: "", passport_number: "", nationality: "México" }]);
                setStep(1);
              }}>
                <UserPlus className="w-4 h-4 mr-2" />
                Crear Otro Cliente
              </Button>
              <Link to="/admin/cases">
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  Ver Casos
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="create-client-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </Link>
          <div>
            <h1 className="font-heading text-3xl font-bold text-navy-900">
              Nuevo Cliente
            </h1>
            <p className="text-navy-500">Registra un nuevo cliente y su grupo familiar</p>
          </div>
        </div>

        {/* Staff Warning */}
        {!loadingStaff && !staffComplete && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-900">Equipo incompleto</h3>
                <p className="text-sm text-amber-700">
                  {vendedores.length === 0 && "No hay vendedores registrados. "}
                  {oficiales.length === 0 && "No hay oficiales migratorios registrados. "}
                  <Link to="/admin/staff" className="underline">Agregar staff</Link>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? "bg-emerald-600 text-white" : "bg-navy-100 text-navy-500"
              }`}>
                {s}
              </div>
              <span className={`text-sm ${step >= s ? "text-navy-900" : "text-navy-400"}`}>
                {s === 1 ? "Información" : s === 2 ? "Familia" : "Confirmar"}
              </span>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? "bg-emerald-600" : "bg-navy-200"}`} />}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <AnimatePresence mode="wait">
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white rounded-2xl border border-navy-100 p-6"
              >
                <h2 className="text-xl font-bold text-navy-900 mb-6 flex items-center gap-2">
                  <User className="w-5 h-5 text-emerald-600" />
                  Información del Titular
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label>Nombre Completo *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Nombre del jefe/a de familia"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@ejemplo.com"
                      data-testid="client-email-input"
                    />
                  </div>
                  <div>
                    <Label>Teléfono</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+52 55 1234 5678"
                    />
                  </div>
                  <div>
                    <Label>País de Origen</Label>
                    <Select value={formData.country} onValueChange={(v) => setFormData({ ...formData, country: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tipo de Visa</Label>
                    <Select value={formData.visa_type} onValueChange={(v) => setFormData({ ...formData, visa_type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="B1/B2">B1/B2 (Turista/Negocios)</SelectItem>
                        <SelectItem value="B1">B1 (Negocios)</SelectItem>
                        <SelectItem value="B2">B2 (Turismo)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nivel de Riesgo Inicial</Label>
                    <Select value={formData.initial_risk_level} onValueChange={(v) => setFormData({ ...formData, initial_risk_level: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Bajo - Perfil sólido</SelectItem>
                        <SelectItem value="medium">Medio - Requiere preparación</SelectItem>
                        <SelectItem value="high">Alto - Caso complejo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nivel de Servicio *</Label>
                    <Select value={formData.plan_type} onValueChange={(v) => setFormData({ ...formData, plan_type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="prueba">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-amber-400 animate-pulse"></span>
                            <span>PRUEBA - Gratis</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="estandar">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-gray-400"></span>
                            <span>ESTÁNDAR - $19.99</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="premium">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                            <span>PREMIUM - $49.99</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="vip">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                            <span>VIP ENTERPRISE - $99.99</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-navy-400 mt-1">
                      {formData.plan_type === "prueba" && "Modo prueba: El cliente explora la plataforma. Conviértelo después."}
                      {formData.plan_type === "estandar" && "Proceso estándar: 3-7 días de respuesta, hasta 9 semanas"}
                      {formData.plan_type === "premium" && "Proceso acelerado: 2-4 días de respuesta, 4-5 semanas"}
                      {formData.plan_type === "vip" && "Proceso VIP: Respuesta en 24h, proceso ultra-acelerado"}
                    </p>
                  </div>
                  
                  {/* VIP Partner Option */}
                  <div className="p-4 rounded-xl border-2 border-dashed border-amber-200 bg-amber-50/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
                          <Crown className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <Label className="text-base font-semibold text-navy-900 cursor-pointer" htmlFor="vip-partner-toggle">
                            VIP Partner
                          </Label>
                          <p className="text-xs text-navy-500">
                            Puede generar códigos de referido y ganar comisiones
                          </p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="vip-partner-toggle"
                          checked={formData.is_vip_partner}
                          onChange={(e) => setFormData({ ...formData, is_vip_partner: e.target.checked })}
                          className="sr-only peer"
                          data-testid="vip-partner-toggle"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                      </label>
                    </div>
                    {formData.is_vip_partner && (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-amber-200">
                        <p className="text-xs text-amber-700">
                          <strong>✨ Se generará automáticamente:</strong>
                        </p>
                        <ul className="text-xs text-amber-600 mt-1 space-y-1">
                          <li>• Código único de referido</li>
                          <li>• Acceso al panel de comisiones</li>
                          <li>• Descuentos especiales en planes</li>
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-emerald-600" />
                      Personas Autorizadas *
                    </Label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={formData.authorized_persons}
                        onChange={(e) => setFormData({ ...formData, authorized_persons: Math.max(1, parseInt(e.target.value) || 1) })}
                        className="w-20 text-center"
                        data-testid="authorized-persons-input"
                      />
                      <span className="text-sm text-navy-500">
                        {formData.authorized_persons === 1 
                          ? "Solo el titular" 
                          : `Titular + ${formData.authorized_persons - 1} familiar${formData.authorized_persons > 2 ? 'es' : ''}`}
                      </span>
                    </div>
                    <p className="text-xs text-navy-400 mt-1">
                      Máximo de personas que puede incluir este caso
                    </p>
                  </div>
                  <div>
                    <Label>Contraseña</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder="Auto-generada si vacía"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                      <Button type="button" variant="outline" onClick={generatePassword}>
                        <Key className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <Button
                    type="button"
                    onClick={() => {
                      if (!formData.name || !formData.email) {
                        toast.error("Nombre y email son requeridos");
                        return;
                      }
                      setStep(2);
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Siguiente: Familia
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Family Members */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white rounded-2xl border border-navy-100 p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-navy-900 flex items-center gap-2">
                    <Users className="w-5 h-5 text-emerald-600" />
                    Miembros del Caso ({familyMembers.length})
                  </h2>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addFamilyMember}
                    data-testid="add-family-member-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Familiar
                  </Button>
                </div>

                <div className="space-y-4">
                  {familyMembers.map((member, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`border rounded-xl p-4 ${
                        index === 0 ? "border-emerald-200 bg-emerald-50" : "border-navy-200"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <span className={`text-sm font-medium ${
                          index === 0 ? "text-emerald-700" : "text-navy-700"
                        }`}>
                          {index === 0 ? "👑 Titular (Jefe/a de Familia)" : `Familiar ${index}`}
                        </span>
                        {index > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFamilyMember(index)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label>Nombre Completo *</Label>
                          <Input
                            value={member.name}
                            onChange={(e) => updateFamilyMember(index, "name", e.target.value)}
                            placeholder="Nombre completo"
                            disabled={index === 0}
                            data-testid={`family-member-name-${index}`}
                          />
                        </div>
                        <div>
                          <Label>Parentesco</Label>
                          <Select
                            value={member.relationship}
                            onValueChange={(v) => updateFamilyMember(index, "relationship", v)}
                            disabled={index === 0}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {RELATIONSHIPS.filter(r => index === 0 ? r.id === "titular" : r.id !== "titular").map(r => (
                                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Fecha de Nacimiento</Label>
                          <Input
                            type="date"
                            value={member.birth_date}
                            onChange={(e) => updateFamilyMember(index, "birth_date", e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>No. Pasaporte</Label>
                          <Input
                            value={member.passport_number}
                            onChange={(e) => updateFamilyMember(index, "passport_number", e.target.value)}
                            placeholder="Ej: G12345678"
                          />
                        </div>
                        <div>
                          <Label>Nacionalidad</Label>
                          <Select
                            value={member.nationality}
                            onValueChange={(v) => updateFamilyMember(index, "nationality", v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="flex justify-between mt-6">
                  <Button type="button" variant="outline" onClick={() => setStep(1)}>
                    Anterior
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setStep(3)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Siguiente: Confirmar
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Review & Confirm */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white rounded-2xl border border-navy-100 p-6"
              >
                <h2 className="text-xl font-bold text-navy-900 mb-6 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  Revisar y Confirmar
                </h2>

                {/* Summary */}
                <div className="p-4 bg-navy-50 rounded-xl">
                  <h3 className="font-medium text-navy-900 mb-4">Resumen del Caso</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-3">
                      <div>
                        <span className="text-navy-500">Titular:</span>
                        <p className="font-medium text-navy-900">{formData.name}</p>
                      </div>
                      <div>
                        <span className="text-navy-500">Email:</span>
                        <p className="font-medium text-navy-900">{formData.email}</p>
                      </div>
                      <div>
                        <span className="text-navy-500">Teléfono:</span>
                        <p className="font-medium text-navy-900">{formData.phone || "No especificado"}</p>
                      </div>
                      <div>
                        <span className="text-navy-500">País:</span>
                        <p className="font-medium text-navy-900">{formData.country}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <span className="text-navy-500">Miembros en el caso:</span>
                        <p className="font-medium text-navy-900">{familyMembers.length} persona(s)</p>
                      </div>
                      <div>
                        <span className="text-navy-500">Personas autorizadas:</span>
                        <p className="font-medium text-navy-900">{formData.authorized_persons}</p>
                      </div>
                      <div>
                        <span className="text-navy-500">Tipo de Visa:</span>
                        <p className="font-medium text-navy-900">{formData.visa_type}</p>
                      </div>
                      <div>
                        <span className="text-navy-500">Nivel de Servicio:</span>
                        <p className={`font-medium ${
                          formData.plan_type === "vip" ? "text-amber-600" : 
                          formData.plan_type === "premium" ? "text-blue-600" : 
                          formData.plan_type === "prueba" ? "text-amber-500" : "text-gray-600"
                        }`}>
                          {formData.plan_type === "vip" ? "VIP - $99.99" : 
                           formData.plan_type === "premium" ? "PREMIUM - $49.99" : 
                           formData.plan_type === "prueba" ? "PRUEBA - Gratis" : "ESTÁNDAR - $19.99"}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* VIP Partner Badge */}
                  {formData.is_vip_partner && (
                    <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2">
                        <Crown className="w-5 h-5 text-amber-600" />
                        <span className="font-medium text-amber-900">VIP Partner</span>
                      </div>
                      <p className="text-xs text-amber-700 mt-1">
                        Se generará código de referido automáticamente
                      </p>
                    </div>
                  )}

                  {/* Family Members List */}
                  <div className="mt-4 pt-4 border-t border-navy-200">
                    <h4 className="text-sm font-medium text-navy-700 mb-2">Miembros del Caso:</h4>
                    <div className="space-y-1">
                      {familyMembers.map((member, idx) => (
                        <div key={idx} className="text-sm text-navy-600">
                          {idx + 1}. {member.name} - {RELATIONSHIPS.find(r => r.id === member.relationship)?.name || member.relationship}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between mt-6">
                  <Button type="button" variant="outline" onClick={() => setStep(2)}>
                    Anterior
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="create-client-submit"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Creando...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Crear Cliente
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default AdminCreateClient;
